import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import axios, { isAxiosError, AxiosResponse, AxiosRequestConfig } from 'axios';

import { useRouter } from '../../routes/hooks';

const httpBootingEnabled = import.meta.env.VITE_HTTP_RESPONSE_BOOTING === 'ON';
const SPOOFING_TOKEN_KEY = 'userSpoofingToken';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface DjangoRequestOptions {
  method: HttpMethod;
  route: string;
  data?: any;
  params?: Record<string, any>;
  additionalHeaders?: Record<string, string>;
}

export function useDjangoApi() {
  const router = useRouter();
  const { logout, getAccessTokenSilently } = useAuth0();
  const { replace } = useRouter();
  const [userSpoofingToken, setUserSpoofingToken] = useState<{ token: string, id: string } | null>(() => {
    // Initialize from localStorage
    const savedToken = localStorage.getItem(SPOOFING_TOKEN_KEY);
    return savedToken ? JSON.parse(savedToken) : null;
  });



  const makeRequest = async <T = any>({
    method,
    route,
    data,
    params,
    additionalHeaders = {}
  }: DjangoRequestOptions): Promise<AxiosResponse<T> | null> => {
    try {
      const token = await getAccessTokenSilently();
      const url = `${import.meta.env.VITE_DJANGO_API_URL}${route.startsWith('/') ? route : `/${route}`}`;
      const config: AxiosRequestConfig = {
        method,
        url,
        data,
        params,
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...(userSpoofingToken && { 'X-Spoofing-Token': userSpoofingToken.token }),
          ...additionalHeaders
        }
      };

      // Only set Content-Type to application/json if data is not FormData
      if (!(data instanceof FormData)) {
        config.headers!['Content-Type'] = 'application/json';
      }

      return await executeRequestWithRetry(config, logout, replace, 1);
    } catch (error) {
      console.error('Request failed:', error);
      enqueueSnackbar('Failed to authenticate request', { variant: 'error' });
      return null;
    }
  };

  // Convenience methods for common HTTP operations
  const get = <T = any>(route: string, params?: Record<string, any>, additionalHeaders?: Record<string, string>) =>
    makeRequest<T>({ method: 'GET', route, params, additionalHeaders });

  const post = <T = any>(route: string, data?: any, additionalHeaders?: Record<string, string>) =>
    makeRequest<T>({ method: 'POST', route, data, additionalHeaders });

  const put = <T = any>(route: string, data?: any, additionalHeaders?: Record<string, string>) =>
    makeRequest<T>({ method: 'PUT', route, data, additionalHeaders });

  const patch = <T = any>(route: string, data?: any, additionalHeaders?: Record<string, string>) =>
    makeRequest<T>({ method: 'PATCH', route, data, additionalHeaders });

  const del = <T = any>(route: string, additionalHeaders?: Record<string, string>) =>
    makeRequest<T>({ method: 'DELETE', route, additionalHeaders });

  const createSpoofingToken = async (user_id: string) => {
    const response = await post<{ token: string, id: string }>('auth/spoofing/create-token/', { user_id, duration_hours: 24 });
    if (response && response.status === 201) {
      const newToken = { id: response.data.id, token: response.data.token }
      setUserSpoofingToken(newToken);
      localStorage.setItem(SPOOFING_TOKEN_KEY, JSON.stringify(newToken));
      router.reload();
    } else {
      enqueueSnackbar('Failed to create spoofing token', { variant: 'error' });
    }
  }

  const removeSpoofingToken = () => {
    setUserSpoofingToken(null);
    localStorage.removeItem(SPOOFING_TOKEN_KEY);
  }





  return {
    request: makeRequest,
    get,
    post,
    put,
    patch,
    delete: del,
    // setSpoofingToken,
    removeSpoofingToken,
    createSpoofingToken,
  };
}

const executeRequestWithRetry = async (
  config: AxiosRequestConfig,
  logout: () => void,
  replace: (path: string) => void,
  attempt: number
): Promise<AxiosResponse | null> => {
  try {
    return await axios(config);
  } catch (error) {
    if (isAxiosError(error)) {
      if (!error.response) {
        enqueueSnackbar('Network error. Please check your internet connection.', { variant: 'error' });
        return null;
      }

      const { status } = error.response;

      if (httpBootingEnabled) {
        handleHttpStatus(status, logout, replace);
        if (status >= 500 && status <= 599) {
          return retryWithBackoff(config, logout, replace, attempt);
        }
        return null;
      } else {
        enqueueSnackbar(`HTTP error: ${status}`, { variant: 'warning' });
      }
    }
    return null;
  }
};

const retryWithBackoff = async (
  config: AxiosRequestConfig,
  logout: () => void,
  replace: (path: string) => void,
  attempt: number
): Promise<AxiosResponse | null> => {
  if (attempt > 3) {
    enqueueSnackbar('Final attempt failed. No more retries.', { variant: 'error' });
    replace('/500');
    return null;
  }

  const delay = (2 ** attempt) * 1000;
  enqueueSnackbar(`Attempt ${attempt} failed. Retrying in ${delay / 1000}s...`, { variant: 'warning' });

  return new Promise((resolve) =>
    setTimeout(async () => {
      const result = await executeRequestWithRetry(config, logout, replace, attempt + 1);
      resolve(result);
    }, delay)
  );
};

const handleHttpStatus = (
  status: number,
  logout: () => void,
  replace: (path: string) => void
): void => {
  switch (status) {
    case 400:
      enqueueSnackbar('Bad request. Please check your input.', { variant: 'warning' });
      break;

    case 401:
      enqueueSnackbar('You have been logged out. Please log in again.', { variant: 'error' });
      setTimeout(() => logout(), 1500);
      break;

    case 403:
      enqueueSnackbar('You do not have permission to access this resource.', { variant: 'warning' });
      replace('/403');
      break;

    case 404:
      enqueueSnackbar('The requested resource was not found.', { variant: 'error' });
      replace('/404');
      break;

    case 418:
      enqueueSnackbar("I'm a teapot!", { variant: 'info' });
      break;

    case 429:
      enqueueSnackbar('Too many requests. Please try again later.', { variant: 'warning' });
      break;

    case 500:
    case 502:
    case 503:
    case 504:
      // retry logic handled separately
      break;

    default:
      enqueueSnackbar(`Unexpected HTTP response: ${status}`, { variant: 'error' });
      console.log('Unexpected response:', status);
  }
};
